import React, { useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { KeyRound, Loader2 } from 'lucide-react';
import { BOTON_VERDE, cuando } from './comun';

/**
 * La clave de la API de Claude. Se pega una vez y se guarda en el servidor:
 * la pantalla nunca la vuelve a ver (el servidor no la devuelve) y, tras
 * guardarla, el campo se vacía. Solo se ve si está puesta, quién y cuándo.
 */
export default function ClaveBot({ bot, onCambio }) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [quitando, setQuitando] = useState(false);
  const puesta = !!bot.clave_puesta && !bot.clave_ilegible;

  const guardar = async (e) => {
    e?.preventDefault();
    const limpia = valor.trim();
    if (!limpia || guardando) return;
    setGuardando(true);
    try {
      const r = await api.put('/api/whatsapp/bot/clave', { valor: limpia });
      setValor('');
      setEditando(false);
      onCambio(r.data);
      toast.success('Clave de Claude guardada');
    } catch (err) {
      toast.error(mensajeError(err, 'No se ha podido guardar la clave'));
    } finally {
      setGuardando(false);
    }
  };

  const quitar = async () => {
    if (quitando || !window.confirm('¿Quitar la clave de la API de Claude? El bot se apaga: no contesta ni '
      + 'prepara borradores hasta que se pegue otra.')) return;
    setQuitando(true);
    try {
      const r = await api.delete('/api/whatsapp/bot/clave');
      onCambio(r.data);
      toast.success('Clave quitada: el bot está apagado');
    } catch (err) {
      toast.error(mensajeError(err, 'No se ha podido quitar la clave'));
    } finally {
      setQuitando(false);
    }
  };

  const cancelar = () => { setValor(''); setEditando(false); };

  return (
    <div className="space-y-2">
      {puesta ? (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="bot-clave-puesta">
          <KeyRound className="w-4 h-4 text-[#2E6E5E]" aria-hidden="true" />
          <span className="font-medium text-emerald-700">Puesta ✓</span>
          {bot.clave_por || bot.clave_at ? (
            <span className="text-muted-foreground">
              {[bot.clave_por ? `la puso ${bot.clave_por}` : '', bot.clave_at ? `el ${cuando(bot.clave_at)}` : '']
                .filter(Boolean).join(' ')}
            </span>
          ) : null}
          {!editando ? (
            <>
              <Button type="button" variant="outline" size="sm" onClick={() => setEditando(true)}
                data-testid="bot-clave-cambiar">Cambiar</Button>
              <Button type="button" variant="ghost" size="sm" onClick={quitar} disabled={quitando}
                className="text-red-700 hover:text-red-800" data-testid="bot-clave-quitar">Quitar</Button>
            </>
          ) : null}
        </div>
      ) : bot.clave_ilegible ? (
        <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded px-2.5 py-1.5" role="alert"
          data-testid="bot-clave-ilegible">
          La clave guardada no se puede leer (por ejemplo, si ha cambiado la configuración del servidor): vuelve a
          pegarla. Mientras, el bot está apagado.
        </p>
      ) : (
        <p className="text-sm text-amber-800" data-testid="bot-clave-sin-poner">
          Sin poner: hasta que se pegue, el bot está apagado.
        </p>
      )}

      {!puesta || editando ? (
        <form onSubmit={guardar} className="flex flex-wrap items-center gap-2" data-testid="bot-clave-formulario">
          <label htmlFor="bot-clave-input" className="sr-only">Clave de la API de Claude</label>
          <Input id="bot-clave-input" type="password" autoComplete="off" spellCheck={false} value={valor}
            onChange={(e) => setValor(e.target.value)} placeholder="Pega aquí la clave (empieza por sk-ant-)"
            className="max-w-md" data-testid="bot-clave-input" />
          <Button type="submit" size="sm" disabled={guardando || !valor.trim()} className={BOTON_VERDE}
            data-testid="bot-clave-guardar">
            {guardando ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null} Guardar
          </Button>
          {puesta ? (
            <Button type="button" variant="ghost" size="sm" onClick={cancelar}>Cancelar</Button>
          ) : null}
        </form>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Se crea en la consola de Anthropic (API Keys). Se guarda en el servidor y esta pantalla no la vuelve a
        enseñar nunca, ni entera ni en parte; tampoco sale en el registro de actividad.
      </p>
    </div>
  );
}
